import { useEffect, useRef } from "react";

/** Box icon: ticked, dashed (some selected) or empty. Inherits text colour. */
export function CheckboxIcon({
  checked = false,
  indeterminate = false,
  size = 18,
}: {
  checked?: boolean;
  indeterminate?: boolean;
  size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="4" width="16" height="16" rx="4" />
      {indeterminate ? (
        <path d="M8 12h8" />
      ) : (
        checked && <path d="M8.5 12.5l2.5 2.5 4.5-5" />
      )}
    </svg>
  );
}

interface CheckboxProps {
  checked: boolean;
  /** Shows a dash instead of a tick, e.g. "some but not all selected". */
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: React.ReactNode;
  className?: string;
  title?: string;
}

/**
 * Accessible checkbox: a real (visually hidden) input, so keyboard and screen
 * readers work, with the app's box icon drawn on top.
 */
export default function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  disabled = false,
  label,
  className = "",
  title,
}: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={`ib-checkbox${disabled ? " ib-checkbox--disabled" : ""} ${className}`.trim()}
      title={title}
    >
      <input
        ref={ref}
        type="checkbox"
        className="ib-checkbox__input"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        className={`ib-checkbox__box${
          checked || indeterminate ? " ib-checkbox__box--on" : ""
        }`}
      >
        <CheckboxIcon checked={checked} indeterminate={indeterminate} size={20} />
      </span>
      {label != null && <span className="ib-checkbox__label">{label}</span>}
    </label>
  );
}
